/**
 * Authentication-specific Zod schemas
 * For login, signup, and password-related forms
 */

import { z } from 'zod';
import {
    emailSchema,
    passwordSchema,
    nameSchema,
    optionalNameSchema,
    phoneSchema,
    optionalPhoneSchema,
    birthdaySchema,
    optionalBirthdaySchema,
    requiredCheckboxSchema,
    optionalCheckboxSchema
} from './base-fields';

/**
 * Password confirmation schema
 */
export const passwordConfirmationSchema = z
    .object({
        password: passwordSchema,
        confirmPassword: passwordSchema
    })
    .refine(data => data.password === data.confirmPassword, {
        message: 'Passwords do not match',
        path: ['confirmPassword']
    });

/**
 * Email confirmation schema
 */
export const emailConfirmationSchema = z
    .object({
        email: emailSchema,
        confirmEmail: emailSchema
    })
    .refine(data => data.email === data.confirmEmail, {
        message: 'Email addresses do not match',
        path: ['confirmEmail']
    });

/**
 * Basic login schema
 */
export const loginSchema = z.object({
    email: emailSchema,
    password: passwordSchema
});

/**
 * Basic signup schema without password confirmation
 */
export const baseSignupSchemaFields = z.object({
    firstName: nameSchema,
    lastName: nameSchema,
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: passwordSchema
});

/**
 * Basic signup schema with password confirmation validation
 */
export const baseSignupSchema = baseSignupSchemaFields.refine(data => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword']
});

/**
 * Extended signup schema fields
 */
export const extendedSignupSchemaFields = baseSignupSchemaFields.extend({
    phone: optionalPhoneSchema,
    birthday: optionalBirthdaySchema,
    postalCode: z.string().optional(),
    marketingEmails: optionalCheckboxSchema,
    marketingSms: optionalCheckboxSchema,
    profiling: optionalCheckboxSchema,
    thirdParty: optionalCheckboxSchema,
    termsAndConditions: requiredCheckboxSchema
});

/**
 * Extended signup schema with validation
 */
export const extendedSignupSchema = extendedSignupSchemaFields.refine(data => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword']
});

/**
 * Get signup schema based on locale and requirements
 */
export const getSignupSchema = (
    locale?: string,
    options: {
        requirePhone?: boolean;
        requireBirthday?: boolean;
        requireMarketing?: boolean;
    } = {}
) => {
    const baseFields: any = {
        firstName: nameSchema,
        lastName: nameSchema,
        email: emailSchema,
        password: passwordSchema,
        confirmPassword: passwordSchema
    };

    // Locale-specific requirements
    switch (locale?.toLowerCase()) {
        case 'enus':
            // US requires phone, name, lastname
            baseFields.phone = phoneSchema;
            baseFields.firstName = nameSchema;
            baseFields.lastName = nameSchema;
            break;

        case 'enau':
            // AU requires phone, birthday; optional name, lastname
            baseFields.phone = phoneSchema;
            baseFields.birthday = birthdaySchema;
            baseFields.firstName = optionalNameSchema;
            baseFields.lastName = optionalNameSchema;
            break;

        default:
            // Default: all fields optional except email and password
            baseFields.firstName = optionalNameSchema;
            baseFields.lastName = optionalNameSchema;
            baseFields.phone = optionalPhoneSchema;
            baseFields.birthday = optionalBirthdaySchema;
    }

    // Apply options
    if (options.requirePhone) {
        baseFields.phone = phoneSchema;
    }

    if (options.requireBirthday) {
        baseFields.birthday = birthdaySchema;
    }

    if (options.requireMarketing) {
        baseFields.marketingEmails = requiredCheckboxSchema;
        baseFields.marketingSms = requiredCheckboxSchema;
    }

    const schema = z.object(baseFields);

    return schema.refine(data => data.password === data.confirmPassword, {
        message: 'Passwords do not match',
        path: ['confirmPassword']
    });
};

/**
 * Password reset request schema
 */
export const passwordResetRequestSchema = z.object({
    email: emailSchema
});

/**
 * Password reset schema
 */
export const passwordResetSchema = z
    .object({
        token: z.string().min(1, 'Reset token is required'),
        password: passwordSchema,
        confirmPassword: passwordSchema
    })
    .refine(data => data.password === data.confirmPassword, {
        message: 'Passwords do not match',
        path: ['confirmPassword']
    });

/**
 * Change password schema
 */
export const changePasswordSchema = z
    .object({
        currentPassword: passwordSchema,
        newPassword: passwordSchema,
        confirmNewPassword: passwordSchema
    })
    .refine(data => data.newPassword === data.confirmNewPassword, {
        message: 'New passwords do not match',
        path: ['confirmNewPassword']
    });

/**
 * Email verification schema
 */
export const emailVerificationSchema = z.object({
    email: emailSchema,
    verificationCode: z.string().min(1, 'Verification code is required')
});

// Type inference
export type LoginFormData = z.infer<typeof loginSchema>;
export type SignupFormData = z.infer<typeof baseSignupSchema>;
export type ExtendedSignupFormData = z.infer<typeof extendedSignupSchema>;
export type PasswordResetRequestFormData = z.infer<typeof passwordResetRequestSchema>;
export type PasswordResetFormData = z.infer<typeof passwordResetSchema>;
export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;
export type EmailVerificationFormData = z.infer<typeof emailVerificationSchema>;
